<script setup lang="ts">
  import Questionaire from '@/components/questionaire/questionaireForm'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { createInstructions, getFormConfig } from '@/lib/utils'
  import type { Item, Page } from '@/types'
  import { genericErrorToast } from '@/lib/toasts'

  const route = useRoute()
  const clientActionsStore = useClientActionStore()
  const isDataLoaded = ref(false)
  const submissionId = computed(() => route.params.uuid as string)
  const submissions = computed(() => clientActionsStore.clientAction?.submissions)
  const hasActions = computed(() => clientActionsStore.clientAction !== null)
  const baseformConfig = computed(() => {
    if (submissions.value) {
      const config = getFormConfig(submissions.value)
      return config || { items: [], activeFieldId: '', title: '', totalQuestionCount: 0 }
    }
    return { items: [], activeFieldId: '', title: '', totalQuestionCount: 0 }
  })

  if (submissionId.value) {
    clientActionsStore.setCurrentSubmissionId(submissionId.value)
  }

  watch(submissionId, (newId) => {
    if (newId) {
      clientActionsStore.setCurrentSubmissionId(newId)
    }
  })

  const InformationformConfig: Item[] = [
    {
      type: 'information',
      label: 'Info',
      text: createInstructions(clientActionsStore.getFirstName, clientActionsStore.getLastName),
      buttonText: 'Apply here',
      optional: true,
      help: '',
      item: '',
      value: '',
      _errors: '',
      choices: [],
      _choices: ''
    }
  ]

  const formConfig = computed(() => [...baseformConfig.value.items])
  const hasFormItems = computed(() => formConfig.value.length > 0)
  const metadata = computed(() => ({
    activeField: baseformConfig.value.activeFieldId,
    title: baseformConfig.value.title,
    totalQuestionCount: baseformConfig.value.totalQuestionCount + 1,
    isPreview: true,
    isExternal: false
  }))
  const questionnaire_id = computed(() => route.params.uuid as string | '')
  onMounted(async () => {
    try {
      console.log('Questionnaire ID', questionnaire_id.value)
      const status = await clientActionsStore.fetchClientActionsPreview(questionnaire_id.value)
      if (status !== 200) {
        genericErrorToast()
      } else {
        isDataLoaded.value = true
      }
    } catch (error) {
      console.error('Error fetching client actions:', error)
      genericErrorToast()
    }
  })

  const currentSubmission = computed(() =>
    submissions.value ? submissions.value[0] : { schema_: [] }
  )

  const rawSubmission = computed(() => currentSubmission.value.schema_ as Page[] | [])
</script>

<template>
  <div class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan">
    <!-- Show loading text when data is not yet loaded -->
    <div
      v-if="!isDataLoaded"
      class="flex h-screen items-center justify-center"
    >
      <LoaderCircle
        :size="100"
        class="animate-spin"
      />
    </div>

    <!-- Display components based on the condition -->
    <template v-else>
      <!-- Display Questionaire if data is loaded, client actions exist, and there are form items -->
      <Questionaire
        v-if="hasActions && hasFormItems"
        :formConfig="formConfig"
        :metaData="metadata"
        :rawSchema="rawSubmission"
      />

      <!-- Display client404 if there are no client actions or form items -->
      <client404 v-else />
    </template>
  </div>
</template>
